import { FC, useEffect, useState } from 'react';
import {
  Button,
  Card,
  Divider,
  InfiniteScrollMultiSelect,
  RadixTable,
} from '@features/ui';
import {
  periodOptions,
  groupByOptions,
} from '@features/admin/reports/reportData';
import {
  ReportGroupBy,
  ReportPeriod,
  TReportRefundsByUsedCouponData,
  TReportRefundsByUsedCouponParams,
} from '@src/types/reports';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { useReportFilters } from '@hooks/useReportFilters';
import { useReportTableColumns } from '@hooks/useReportTableColumns';
import { useReportChartData, getChartColor } from '@hooks/useReportChartData';
import { buildCommonPayloadWithGrouping } from '@src/utils/buildCommonPayload';
import {
  exportRefundsByUsedCoupon,
  getRefundsByUsedCoupon,
} from '@src/services/reports';
import { downloadFile } from '@src/lib/utils';
import { useCouponInfiniteOptions } from '@src/hooks/useCouponInfiniteOptions';
import { toast } from 'react-toastify';
import { canAccess } from '@src/utils/auth';
import { UserRolePermission } from '@src/types/settings';
import { useAuthStore } from '@src/stores/authStore';

import { ReportFilteringSettings } from '../ReportFilteringSettings';

interface TooltipProps {
  active?: boolean;
  payload?: Array<{
    value: number;
    dataKey: string;
    color: string;
    payload: any;
    name: string;
  }>;
}

const CustomTooltip: FC<TooltipProps> = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div
        className="bg-card-stat p-2.5 rounded-lg text-xs border border-[#3D3D3D] flex flex-col gap-y-1 min-w-[200px]"
        style={{ boxShadow: '0px 7px 13px 0px #B6D72C14' }}
      >
        <p className="text-[#999999] text-[10px] text-center mb-1">
          {payload[0]?.payload?.date}
        </p>
        {payload.map((entry, index) => (
          <div key={index} className="flex justify-between items-center gap-4">
            <span className="text-gray-300 text-xs">{entry.name}:</span>
            <span
              className="text-white font-semibold"
              style={{ color: entry.color }}
            >
              ${entry.value.toLocaleString()}
            </span>
          </div>
        ))}
      </div>
    );
  }

  return null;
};

export const RefundsByUsedCoupon: FC = () => {
  const { auth } = useAuthStore();
  const [data, setData] = useState<TReportRefundsByUsedCouponData[]>([]);

  const { couponOptions, loading, hasMore, loadMore } =
    useCouponInfiniteOptions();

  const defaultSelectedCouponBatch =
    couponOptions.length > 0 ? couponOptions[0].value : '';

  const filters = useReportFilters({
    defaultPeriod: ReportPeriod.LAST_7_DAYS,
    defaultGroupBy: ReportGroupBy.DAY,
    enableComparisons: true,
  });

  const customColumns = [
    {
      title: 'Refund Amount, USD',
      className: 'text-right',
      render: (item?: TReportRefundsByUsedCouponData) => {
        if (!item) return null;

        return (
          <p className="text-white">${(item.refund_amount ?? 0).toLocaleString()}</p>
        );
      },
    },
  ];

  const { columns, tableData } = useReportTableColumns({
    data,
    selectedItems: [],
    compareToPreviousPeriod: filters.compareToPreviousPeriod,
    compareToSamePeriodPreviousYear: filters.compareToSamePeriodPreviousYear,
    getAccessField: (item) => item.date,
    getMainDataField: (item, _) => item.refund_amount,
    getPreviousPeriodField: (item, _) => item.refund_amount_previous,
    getPreviousYearField: (item, _) => item.refund_amount_previous_year,
    includeTotalsRow: true,
    customColumns,
  });

  const displayItems = ['refund_amount'];

  const chartData = useReportChartData({
    data,
    displayItems,
    compareToPreviousPeriod: filters.compareToPreviousPeriod,
    compareToSamePeriodPreviousYear: filters.compareToSamePeriodPreviousYear,
    getAccessField: (item) => item.date,
    getMainDataField: (item, _) => item.refund_amount,
    getPreviousPeriodField: (item, _) => item.refund_amount_previous,
    getPreviousYearField: (item, _) => item.refund_amount_previous_year,
  });

  const buildPayload = (): TReportRefundsByUsedCouponParams => {
    const commonPayload = buildCommonPayloadWithGrouping(filters);
    const selectedBatch = filters.selectedCoupon || defaultSelectedCouponBatch;

    return {
      start_date: commonPayload.start_date,
      end_date: commonPayload.end_date,
      group: commonPayload.grouping,
      compare_to_previous_period: commonPayload.compare_to_previous_period,
      compare_to_same_period_previous_year:
        commonPayload.compare_to_same_period_previous_year,
      coupon_batch: selectedBatch ? [Number(selectedBatch)] : undefined,
    };
  };

  const handleRunReport = async () => {
    filters.setIsLoading(true);
    try {
      const payload = buildPayload();
      const response = await getRefundsByUsedCoupon(payload);

      setData(response.data || []);
      toast.success('Report generated successfully!');
    } catch (error) {
      console.error('Error fetching report:', error);
      toast.error('Failed to generate report. Please try again.');
    } finally {
      filters.setIsLoading(false);
    }
  };

  const handleExportReport = async () => {
    try {
      const payload = buildPayload();
      const response = await exportRefundsByUsedCoupon(payload);

      downloadFile(response.data, 'refunds-by-used-coupon-code');
      toast.success('Report exported successfully!');
    } catch (error) {
      console.error('Error exporting report:', error);
      toast.error('Failed to export report. Please try again.');
    }
  };

  useEffect(() => {
    handleRunReport();
  }, []);

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-white">
        Refunds By Used Coupon
      </h1>

      <Card className="p-6">
        <ReportFilteringSettings
          compareToPreviousPeriod={filters.compareToPreviousPeriod}
          compareToSamePeriodPreviousYear={
            filters.compareToSamePeriodPreviousYear
          }
          dateRangeValue={filters.valueStart}
          groupByOptions={groupByOptions}
          isLoading={filters.isLoading}
          periodOptions={periodOptions}
          periodRange={filters.periodRange}
          selectedGroupBy={filters.selectedGroupBy}
          selectedPeriod={filters.selectedPeriod}
          showCompareToPreviousPeriod={filters.showPreviousPeriodCheckbox}
          showCompareToSamePeriodPreviousYear={filters.showPreviousYearCheckbox}
          onCompareToPreviousPeriodChange={filters.setCompareToPreviousPeriod}
          onCompareToSamePeriodPreviousYearChange={
            filters.setCompareToSamePeriodPreviousYear
          }
          onDateRangeChange={filters.setValueStart}
          onGroupByChange={filters.setSelectedGroupBy}
          onPeriodChange={filters.setSelectedPeriod}
          onRunReport={handleRunReport}
        >
          <div className="flex items-center">
            <label
              className="text-white min-w-[250px] text-sm"
              htmlFor="coupon-batch-select"
            >
              Coupon Batch <br />
              <span className="text-gray-400">Keep empty to report all</span>
            </label>
            <div className="flex-1 max-w-[300px]">
              <InfiniteScrollMultiSelect
                hasMore={hasMore}
                id="coupon-batch-select"
                isLoading={loading}
                isMulti={false}
                options={couponOptions}
                placeholder="-- Please Select --"
                value={filters.selectedCoupon || defaultSelectedCouponBatch}
                onChange={(value) =>
                  filters.setSelectedCoupon(value?.toString() || '')
                }
                onMenuScrollToBottom={loadMore}
              />
            </div>
          </div>
        </ReportFilteringSettings>
      </Card>

      <Card className="p-6">
        <div className="space-y-4">
          <ResponsiveContainer height={400} width="100%">
            <LineChart data={chartData}>
              <CartesianGrid stroke="#333" strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                stroke="#999"
                style={{ fontSize: '12px' }}
              />
              <YAxis
                stroke="#999"
                style={{ fontSize: '12px' }}
                tickFormatter={(value) => `$${value.toLocaleString()}`}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                iconType="line"
                wrapperStyle={{ fontSize: '12px', paddingTop: '20px' }}
              />

              {displayItems.map((item, index) => (
                <Line
                  key={item}
                  activeDot={{ r: 6 }}
                  dataKey={item}
                  dot={{ fill: getChartColor(index), r: 4 }}
                  name="Refund Amount"
                  stroke={getChartColor(index)}
                  strokeWidth={2}
                  type="monotone"
                />
              ))}

              {filters.compareToPreviousPeriod &&
                displayItems.map((item, index) => (
                  <Line
                    key={`${item}_previous_period`}
                    activeDot={{ r: 4 }}
                    dataKey={`${item}_previous_period`}
                    dot={{
                      fill: getChartColor(index),
                      r: 3,
                    }}
                    name="Refund Amount (Previous Period)"
                    stroke={getChartColor(index)}
                    strokeDasharray="5 5"
                    strokeWidth={1.5}
                    type="monotone"
                  />
                ))}

              {filters.compareToSamePeriodPreviousYear &&
                displayItems.map((item, index) => (
                  <Line
                    key={`${item}_previous_year`}
                    activeDot={{ r: 4 }}
                    dataKey={`${item}_previous_year`}
                    dot={{
                      fill: getChartColor(index),
                      r: 3,
                    }}
                    name="Refund Amount (Previous Year)"
                    stroke={getChartColor(index)}
                    strokeDasharray="2 2"
                    strokeWidth={1.5}
                    type="monotone"
                  />
                ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card className="p-6">
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-white">Detailed Data</h2>
          <RadixTable columns={columns} data={tableData} />
          <Divider className="my-4" />
          {canAccess(
            UserRolePermission.EXPORT_REPORTS,
            auth?.user?.permissions
          ) && (
            <div className="flex justify-end">
              <Button
                className="text-[#B6D72C] border-[#B6D72C] hover:bg-[#B6D72C] hover:text-black"
                variant="outline"
                onClick={handleExportReport}
              >
                Download Excel
              </Button>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};
